import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, BarChart3, Inbox, ArrowLeft, RefreshCw, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ideaforge_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (t) => {
    setRefreshing(true);
    try {
      const [statsRes, ideasRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { params: { token: t } }),
        axios.get(`${API}/admin/ideas`, { params: { token: t } }),
      ]);
      setStats(statsRes.data);
      setRecords(ideasRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please log in.");
      } else {
        toast.error("Failed to load admin data.");
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (token) fetchData(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome, admin.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
    setRecords([]);
  };

  // ----- Login screen -----
  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="absolute top-6 left-6">
          <Link to="/" className="font-sub text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white inline-flex items-center gap-2" data-testid="back-home">
            <ArrowLeft className="w-3 h-3" /> Back to site
          </Link>
        </div>

        <form
          onSubmit={handleLogin}
          className="w-full max-w-md border border-white/10 bg-[#121212] p-10 relative slide-up"
          data-testid="admin-login-form"
        >
          <div className="absolute -top-px left-0 w-12 h-px bg-[#FF3B30]" />
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-sub text-[0.65rem] uppercase tracking-[0.3em] text-white/40">Restricted</div>
              <div className="font-display text-3xl uppercase leading-none">Admin Access</div>
            </div>
          </div>

          <label className="field-label">Password</label>
          <input
            type="password"
            className="field-input"
            placeholder="••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="admin-password-input"
          />

          <button
            type="submit"
            disabled={loading || !password}
            className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : "Enter Dashboard"}
          </button>

          <div className="mt-6 text-center font-sub text-[0.65rem] uppercase tracking-[0.25em] text-white/30">
            Authorized personnel only
          </div>
        </form>
      </div>
    );
  }

  // ----- Dashboard -----
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/50 hover:text-white" data-testid="admin-home-link">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="font-display text-2xl uppercase leading-none">
              Admin <span className="text-[#FF3B30]">Console</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchData(token)}
              className="font-sub text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white inline-flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-white/30 transition-colors"
              data-testid="refresh-btn"
              disabled={refreshing}
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={handleLogout}
              className="font-sub text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white inline-flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-white/30 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-3 h-3" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="stats-grid">
          <StatCard label="Generations" value={stats?.total_generations ?? "—"} icon={<BarChart3 className="w-4 h-4" />} />
          <StatCard label="Total Ideas" value={stats?.total_ideas ?? "—"} icon={<Inbox className="w-4 h-4" />} accent />
          <StatCard label="Top Niche" value={stats?.top_niches?.[0]?.niche || "—"} icon={<BarChart3 className="w-4 h-4" />} />
          <StatCard label="Niche Count" value={stats?.top_niches?.length ?? 0} icon={<BarChart3 className="w-4 h-4" />} />
        </div>

        {/* Top niches list */}
        {stats?.top_niches?.length > 0 && (
          <div className="mb-12 border border-white/10 bg-[#121212] p-6" data-testid="top-niches">
            <div className="font-sub text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Top Niches</div>
            <div className="space-y-3">
              {stats.top_niches.map((n) => (
                <div key={n.niche} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0">
                  <div className="font-sub text-sm">{n.niche}</div>
                  <div className="font-display text-xl text-[#FF3B30]">{n.count}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Records */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-3xl uppercase leading-none tracking-tighter">Generation History</h2>
          <div className="font-sub text-xs uppercase tracking-[0.2em] text-white/40">{records.length} records</div>
        </div>

        {records.length === 0 ? (
          <div className="border border-white/10 bg-[#121212] p-12 text-center" data-testid="empty-history">
            <Inbox className="w-8 h-8 text-white/30 mx-auto mb-3" />
            <div className="font-sub text-sm text-white/50">No generations yet.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="history-list">
            {records.map((r) => (
              <RecordItem key={r.id} record={r} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <div className={`border border-white/10 p-6 ${accent ? "bg-[#FF3B30]" : "bg-[#121212]"}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-4">
        <div className={`font-sub text-[0.65rem] uppercase tracking-[0.25em] ${accent ? "text-white/80" : "text-white/40"}`}>
          {label}
        </div>
        <div className={accent ? "text-white" : "text-white/50"}>{icon}</div>
      </div>
      <div className={`font-display text-5xl leading-none uppercase ${accent ? "text-white" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function RecordItem({ record }) {
  const [open, setOpen] = useState(false);
  const date = record.created_at ? new Date(record.created_at).toLocaleString() : "—";
  return (
    <div className="border border-white/10 bg-[#121212]" data-testid={`record-${record.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full p-5 flex flex-wrap items-center justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-6 flex-wrap">
          <div>
            <div className="font-sub text-[0.6rem] uppercase tracking-[0.25em] text-white/40">Niche</div>
            <div className="font-sub font-bold">{record.niche}</div>
          </div>
          <div>
            <div className="font-sub text-[0.6rem] uppercase tracking-[0.25em] text-white/40">Audience</div>
            <div className="font-sub text-sm text-white/80">{record.audience}</div>
          </div>
          <div>
            <div className="font-sub text-[0.6rem] uppercase tracking-[0.25em] text-white/40">Ideas</div>
            <div className="font-display text-2xl text-[#FF3B30] leading-none">{record.count}</div>
          </div>
        </div>
        <div className="font-sub text-xs uppercase tracking-[0.2em] text-white/40">{date}</div>
      </button>
      {open && (
        <div className="border-t border-white/10 p-5 space-y-3">
          {record.ideas.map((idea, i) => (
            <div key={i} className="border-l-2 border-[#FF3B30] pl-3">
              <div className="font-sub font-bold">{idea.title}</div>
              <div className="font-body text-sm text-white/60 italic">"{idea.hook}"</div>
              <div className="font-body text-xs text-white/40 mt-1">{idea.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
